import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Tag, ShieldOff, Package } from 'lucide-react';
import { formatPrice, altForPart } from '../mock';
import WatchButton from './WatchButton';

export default function PartCard({ p }) {
  const tiers = Array.isArray(p.pricing_tiers) ? p.pricing_tiers : [];
  const hasTiers = tiers.length > 0;
  const lowestTier = hasTiers ? tiers.reduce((a, b) => (Number(a.price) <= Number(b.price) ? a : b)) : null;
  const onSale = p.status === 'on_sale' && p.sale_price;
  const displayPrice = onSale ? Number(p.sale_price) : (lowestTier ? Number(lowestTier.price) : Number(p.price || 0));
  const showFromPrefix = hasTiers && tiers.length > 1;
  const multiples = Number(p.sold_in_multiples_of || 1);
  const qty = p.qty == null ? null : Number(p.qty);

  return (
    <Link to={`/parts/${p.id}`} className="vehicle-card group block bg-white rounded-2xl border border-black/10 overflow-hidden">
      <div className="relative overflow-hidden aspect-[4/3] bg-[#fafafa]">
        {p.images?.[0] ? (
          <img src={p.images[0]} alt={p.name} className="vehicle-img absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-black/25"><Tag size={36} /></div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="bg-white/95 text-black text-[10px] tracking-[0.16em] uppercase font-semibold px-2.5 py-1 rounded-full">{p.category}</span>
          {p.featured && <span className="bg-[#e11d2c] text-white text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full">Featured</span>}
        </div>
        <div className="absolute top-3 right-3 flex gap-2 items-center">
          <WatchButton type="part" id={p.id} />
          {multiples > 1 && (
            <span className="bg-black text-white text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1"><Package size={11} /> Set of {multiples}</span>
          )}
          {onSale && (
            <span className="bg-[#e11d2c] text-white text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 rounded-full">Sale</span>
          )}
          {!p.in_stock && (
            <span className="bg-black/85 text-white text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1"><ShieldOff size={11} /> Sourcing</span>
          )}
        </div>
        <div className="absolute right-3 bottom-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <div className="bg-[#e11d2c] text-white px-3.5 py-2 rounded-full text-[11px] font-semibold inline-flex items-center gap-1.5">
            View details <ArrowUpRight size={13} />
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-wider text-black/50">SKU #{p.sku}</div>
          {qty != null && qty > 0 && qty <= 10 && (
            <div className="text-[10px] tracking-wider uppercase font-bold text-amber-600">Only {qty} left</div>
          )}
        </div>
        <div className="font-display text-[17px] font-bold text-black mt-0.5 leading-tight">{p.name}</div>
        {p.fits && <div className="text-[12px] text-black/55 mt-1 line-clamp-1">Fits: {p.fits}</div>}
        <div className="flex items-end justify-between mt-3">
          <div className="text-[11px] text-black/55">{p.brand || ''}</div>
          <div className="text-right">
            {onSale && (
              <div className="text-[11px] text-black/40 line-through leading-none">{formatPrice(p.price)}</div>
            )}
            <div className="font-display text-[18px] font-bold text-[#e11d2c] leading-none">
              {displayPrice > 0
                ? <>{showFromPrefix && <span className="text-[10px] tracking-wider uppercase text-black/45 font-bold mr-1">From</span>}{formatPrice(displayPrice)}</>
                : 'Call'}
            </div>
            {hasTiers && (
              <div className="text-[10px] text-black/50 mt-1">{tiers.length} bulk price{tiers.length === 1 ? '' : 's'}</div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
